import { degrees, PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import receiptPdf from './assets/sport_processing_receipt_2025-03-12.pdf'
import productOfCanadaPng from './assets/product_of_canada.png'
import manifestLabelPdf from './assets/manifest-label-template_2024-05-13.pdf'
import { DateTime } from "luxon";
import { ContactService } from "./services/contactService.js"
import JsBarcode from "jsbarcode"
import QRCode from 'qrcode'
import { useEffect, useRef } from 'react';
import { fetchSignatureBlob } from "./utils/fetchSignatureBlob.js"

function sortWeighInDetailsFunc(weighInProcesses) {
    return (a, b) => {
        const aProcess = weighInProcesses.find(x => x.id == a.weigh_in_process_id)
        const bProcess = weighInProcesses.find(x => x.id == b.weigh_in_process_id)
        if (!aProcess) {
            return 1
        }
        if (!bProcess) {
            return -1
        }
        if (aProcess.process_id !== bProcess.process_id) {
            return aProcess.process_id - bProcess.process_id
        }
        if (a.species_id !== b.species_id) {
            return a.species_id - b.species_id
        }
        return a.weigh_in_process_id - b.weigh_in_process_id
    }
}

function lastNameCommaFirstName(address) {
    if (!address) {
        return ""
    }
    if (address.last_name) {
        if (address.first_name) {
            return address.last_name + ", " + address.first_name
        }
        return address.last_name
    }
    if (address.first_name) {
        return address.first_name
    }
    return ""
}

function address(order) {
    if (order?.pick_up_location_id) {
        return order?.bill_to
    }
    if (order?.bill_same_as_ship || !order?.bill_to) {
        return order?.ship_to
    }
    return order?.bill_to
}

function billAddress(order) {
    return address(order)
}

function shipAddress(order) {
    return order?.ship_to
}

function getOriginalLocation(order, locations) {
    if (order?.lodge_id) {
        return locations.find(x => x.id === order.lodge_id)
    }
    if (order?.location_id) {
        return locations.find(x => x.id === order.location_id)
    }
    return null
}

function formatPhone(value) {
    return ContactService.formatPhone(value)
}

function trimSubString(input, substring) {
    var result = input
    if (!input || !substring) {
        return input
    }
    while (true) {
        if (result.startsWith(substring)) {
            result = result.slice(substring.length)
        } else {
            break
        }
    }
    while (true) {
        if (result.endsWith(substring)) {
            result = result.slice(0, substring.length * -1)
        } else {
            break
        }
    }
    return result
}

function formatMoney(amount) {
    if (amount === null) {
        return null
    }
    if (typeof amount === "string") {
        amount = parseFloat(amount)
        if (isNaN(amount)) {
            return null
        }
    }
    if (typeof amount !== "number") {
        return null
    }
    if (amount >= 0) {
        return `$${amount.toFixed(2)}`
    }
    return `-$${(-amount).toFixed(2)}`
}

function formatContactMethod(contactMethod) {
    switch (contactMethod) {
        case "phone":
            return "Phone"
        case "text":
            return "Text"
        default:
            return "Email"
    }
}

function newWeighInDetail() {
    return {
        weigh_in_process_id: null,
        species_id: null,
        weight: null,
        half_lb: null,
        x_species_code: null,
        x_description: null,
        fish_states: null
    }
}

function newHalfLbValue(previousValue, weighInProcess) {
    if (!weighInProcess) {
        return previousValue
    }
    switch (weighInProcess.half_lb_option) {
        case "never":
            return false
        case "always":
            return true
    }
    return previousValue
}

function optionsForProcess(processId, options) {
    return options.filter(o => [o.process_id1, o.process_id2, o.process_id3, o.process_id4, o.process_id5].includes(processId))
}

function calculateWeighInProcess(processId, speciesId, orderOptions, options, weighInProcesses) {
    const possibleWeighInProcesses = weighInProcesses.filter(wip => wip.process_id === processId && wip.tuna_only === (speciesId === 10))
    if (possibleWeighInProcesses.length === 1) {
        return possibleWeighInProcesses[0]
    }
    const relevantOptionIds = orderOptions
        .filter(oo => oo.value && optionsForProcess(processId, options).find(ofp => ofp.id === oo.option_id))
        .map(oo => oo.option_id)
    const matchingWeighInProcesses = possibleWeighInProcesses.filter(wip =>
        [...new Set([wip.pin_out_option_id, wip.skin_off_option_id].filter(x => x))]
            .sort()
            .join(',') === relevantOptionIds.sort().join(',')
    )
    if (!matchingWeighInProcesses || matchingWeighInProcesses.length === 0) {
        return null
    }
    return matchingWeighInProcesses.sort((a, b) => a.id - b.id)[0]
}

function orderDetailToWeighInDetail(orderDetail, orderOptions, options, weighInProcesses) {
    const weighInProcess = calculateWeighInProcess(orderDetail.process_id, orderDetail.species_id, orderOptions, options, weighInProcesses)
    return {
        ...newWeighInDetail(),
        weigh_in_process_id: weighInProcess?.id,
        half_lb: newHalfLbValue(false, weighInProcess),
        species_id: orderDetail.species_id,
        original_num_fish: orderDetail.num_fish
    }
}

export async function showManifestLabelPdf(manifest, location) {
    const existingPdfBytes = await fetch(manifestLabelPdf).then(res => res.arrayBuffer())
    const pdfDoc = await PDFDocument.load(existingPdfBytes)
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
    const pages = pdfDoc.getPages()
    const firstPage = pages[0]
    const { height } = firstPage.getSize()

    let textColor = rgb(0, 0, 0)

    const date = manifest.received_at ? DateTime.fromISO(manifest.received_at) : DateTime.fromISO(manifest.shipped_at)
    const dateStr = date.toFormat('LLL dd / yy')

    firstPage.drawText(dateStr, {
        x: 37,
        y: height - 172,
        size: 24,
        font: helveticaFont,
        color: textColor,
    })

    firstPage.drawText("Sport", {
        x: 37,
        y: height - 256,
        size: 24,
        font: helveticaFont,
        color: textColor,
    })

    firstPage.drawText(location.short_code, {
        x: 37,
        y: height - 340,
        size: 24,
        font: helveticaFont,
        color: textColor,
    })

    firstPage.drawText(manifest.label_description ? manifest.label_description : "", {
        x: 37,
        y: height - 424,
        size: 20,
        font: helveticaFont,
        color: textColor,
    })

    firstPage.drawText(manifest.manifest_num, {
        x: 250,
        y: height - 300,
        size: 120,
        font: helveticaFont,
        color: textColor,
    })

    const pdfBytes = await pdfDoc.save()
    var file = new Blob([pdfBytes], { type: 'application/pdf' });
    var fileURL = URL.createObjectURL(file);
    window.open(fileURL);
}

export async function generateReceiptPdfPage(page, helveticaFont, helveticaFontBold, order, master, locations, signaturePdfImage, species, options, weighInProcesses) {
    const { height } = page.getSize()

    let textColor = rgb(0, 0, 0)
    let textNegative = rgb(1, 1, 1)

    page.drawText(getOriginalLocation(order, locations).name ?? "", {
        x: 190,
        y: height - 43,
        size: 11,
        font: helveticaFont,
        color: textColor,
    })

    if (order.ordered_at) {
        const date = new Date(order.ordered_at)
        const month = ((date.getUTCMonth() + 1) + "").padStart(2, '0')
        const day = (date.getUTCDate() + "").padStart(2, '0')
        const year = date.getUTCFullYear() + ""

        page.drawText(`${year}-${month}-${day}`, {
            x: 190,
            y: height - 29,
            size: 11,
            font: helveticaFont,
            color: textColor,
        })
    }

    let yDetails = 24
    if (order.trip_code) {
        page.drawText(`TRIP`, {
            x: 347,
            y: height - yDetails,
            size: 8,
            font: helveticaFontBold,
            color: textColor,
        })

        page.drawText(order.trip_code ?? "ABC", {
            x: 375,
            y: height - yDetails,
            size: 8,
            font: helveticaFont,
            color: textColor,
        })

        yDetails += 15
    }

    if (order.prepaid_amount) {
        page.drawText(`PREPAID`, {
            x: 330,
            y: height - yDetails,
            size: 8,
            font: helveticaFontBold,
            color: textColor,
        })

        page.drawLine({
            start: {
                x: 370,
                y: height - (yDetails + 2),
            },
            end: {
                x: 435,
                y: height - (yDetails + 2),
            },
            thickness: 1,
            color: textColor
        })

        page.drawText(`${formatMoney(order.prepaid_amount)}`, {
            x: 375,
            y: height - (yDetails - 1),
            size: 9,
            font: helveticaFont,
            color: textColor,
        })

        yDetails += 16
    }

    if (order.is_staff) {
        page.drawText(`Lodge Staff`, {
            x: 321,
            y: height - yDetails,
            size: 8,
            font: helveticaFontBold,
            color: textColor,
        })

        page.drawRectangle({
            x: 375,
            y: height - (yDetails + 1),
            width: 8,
            height: 8,
            borderColor: rgb(0, 0, 0),
            borderWidth: 1,
        })

        page.drawText(`x`, {
            x: 376,
            y: height - yDetails,
            size: 11,
            font: helveticaFontBold,
            color: textColor,
        })
    }

    let masterX = 450
    if (master.length > 7) {
        masterX = 405
    }

    page.drawText(master ?? "", {
        x: masterX,
        y: height - 32,
        size: 28,
        font: helveticaFontBold,
        color: textColor,
    })

    // Print CODE128 barcode of master #
    if (master) {
        const data = {}
        JsBarcode(data, master)
        let x = masterX
        let y = height - 60
        for (var i = 0; i < data.encodings[0].data.length; i++) {
            if (data.encodings[0].data[i] === "1") {
                page.drawRectangle({
                    x: x,
                    y: y,
                    width: 1.3,
                    height: 22,
                    color: rgb(0, 0, 0),
                })
            }
            x = x + 1.3
        }
    }

    const billTo = billAddress(order)
    page.drawText(lastNameCommaFirstName(billTo).toUpperCase(), {
        x: 40,
        y: height - 82,
        size: 14,
        font: helveticaFontBold,
        color: textNegative,
    })

    page.drawText(billTo.street_address ? billTo.street_address.toUpperCase() : "", {
        x: 55,
        y: height - 105,
        size: 11,
        font: helveticaFont,
        color: textColor,
    })

    var billToLine2 = trimSubString([billTo.city ?? "", billTo.state ?? ""].join(", "), ", ")
    page.drawText(billToLine2.toUpperCase(), {
        x: 55,
        y: height - 119,
        size: 11,
        font: helveticaFont,
        color: textColor,
    })

    var billToLine3 = trimSubString([billTo.country ?? "", billTo.zip ?? ""].join(", "), ", ")
    page.drawText(billToLine3.toUpperCase(), {
        x: 55,
        y: height - 133,
        size: 11,
        font: helveticaFont,
        color: textColor,
    })

    page.drawText("Fishing License: ", {
        x: 55,
        y: height - 149,
        size: 10,
        font: helveticaFont,
        color: textColor,
    })

    page.drawText(order.fishing_license ?? "", {
        x: 130,
        y: height - 149,
        size: 10,
        font: helveticaFont,
        color: textColor,
    })

    page.drawText(billTo.phone ? formatPhone(billTo.phone) : "", {
        x: 287,
        y: height - 105,
        size: 11,
        font: helveticaFont,
        color: textColor,
    })

    if (billTo.email) {
        let billEmailFontSize = 7 // default for really long emails
        if (billTo.email.length < 20) {
            billEmailFontSize = 10
        } else if (billTo.email.length < 25) {
            billEmailFontSize = 9
        } else if (billTo.email.length < 30) {
            billEmailFontSize = 8
        }
        page.drawText(billTo.email.toUpperCase(), {
            x: 287,
            y: height - 119,
            size: billEmailFontSize,
            font: helveticaFont,
            color: textColor,
        })
    }

    page.drawText("Contact: ", {
        // Right column, in line with fishing license
        x: 287,
        y: height - 149,
        size: 10,
        font: helveticaFont,
        color: textColor,
    })

    page.drawText(formatContactMethod(order.contact_method), {
        x: 328,
        y: height - 149,
        size: 10,
        font: helveticaFont,
        color: textColor,
    })

    if (!order.bill_same_as_ship) {
        const shipTo = shipAddress(order)
        let shipName = trimSubString([shipTo?.last_name ?? "", shipTo?.first_name ?? ""].join(", "), ", ")
        let shipStreet = shipTo?.street_address
        let shipCity = shipTo?.city
        let shipState = shipTo?.state
        let shipPhone = formatPhone(shipTo?.phone ?? "")
        let shipZip = shipTo?.zip
        let shipCountry = shipTo?.country
        let shipEmail = shipTo?.email
        if (order.pick_up_location_id) {
            const pickUpLocation = locations.find(location => location.id === order.pick_up_location_id)
            shipName = "PICK UP IN STORE"
            shipStreet = pickUpLocation.pick_up_name || pickUpLocation.name
            shipCity = ""
            shipState = ""
            shipPhone = ""
            shipZip = ""
            shipCountry = ""
            shipEmail = ""
        }

        const shipHeightOffset = 97

        page.drawText(shipName.toUpperCase(), {
            x: 40,
            y: height - 82 - shipHeightOffset,
            size: 14,
            font: helveticaFontBold,
            color: textColor,
        })

        page.drawText(shipStreet ? shipStreet.toUpperCase() : "", {
            x: 55,
            y: height - 105 - shipHeightOffset,
            size: 11,
            font: helveticaFont,
            color: textColor,
        })

        var shipToLine2 = trimSubString([shipCity ?? "", shipState ?? ""].join(", "), ", ")
        page.drawText(shipToLine2.toUpperCase(), {
            x: 55,
            y: height - 119 - shipHeightOffset,
            size: 11,
            font: helveticaFont,
            color: textColor,
        })

        var shipToLine3 = trimSubString([shipCountry ?? "", shipZip ?? ""].join(", "), ", ")
        page.drawText(shipToLine3.toUpperCase(), {
            x: 55,
            y: height - 133 - shipHeightOffset,
            size: 11,
            font: helveticaFont,
            color: textColor,
        })

        page.drawText(shipPhone ? formatPhone(shipPhone) : "", {
            x: 287,
            y: height - 105 - shipHeightOffset,
            size: 11,
            font: helveticaFont,
            color: textColor,
        })

        if (shipEmail) {
            let shipEmailFontSize = 7 // default for really long emails
            if (shipEmail.length < 20) {
                shipEmailFontSize = 10
            } else if (shipEmail.length < 25) {
                shipEmailFontSize = 9
            } else if (shipEmail.length < 30) {
                shipEmailFontSize = 8
            }
            page.drawText(shipEmail.toUpperCase(), {
                x: 287,
                y: height - 119 - shipHeightOffset,
                size: shipEmailFontSize,
                font: helveticaFont,
                color: textColor,
            })
        }
    }

    page.drawText(`FISH TAG:`, {
        x: 27,
        y: height - 259,
        size: 12,
        font: helveticaFontBold,
        color: textColor,
    })

    page.drawText(`${order.fish_tag ? order.fish_tag : ""}`, {
        x: 95,
        y: height - 259,
        size: 12,
        font: helveticaFont,
        color: textColor,
    })

    if (!order.hold_for_pick_up) {
        const enrichedDetails = order.details.map(detail => {
            const enrichedDetail = orderDetailToWeighInDetail(detail, order.options, options, weighInProcesses)
            enrichedDetail.species = trimSubString(trimSubString(species.find(x => x.id === enrichedDetail.species_id)?.name ?? "(Unknown Species)", "Salmon"), "Cod").trim()

            enrichedDetail.weigh_in_process_name = weighInProcesses.find(x => x.id === enrichedDetail.weigh_in_process_id)?.oms_name ?? "(Unknown Process)"
            enrichedDetail.fish_description = detail.fish_description?.trim()
            return enrichedDetail
        }).sort(sortWeighInDetailsFunc(weighInProcesses))

        const heightOffset = 282
        const rowHeight = 44
        enrichedDetails.forEach((detail, i) => {
            page.drawText(`x${detail.original_num_fish}`, {
                x: 40,
                y: height - heightOffset - (i * rowHeight),
                size: 14,
                font: helveticaFont,
                color: textColor,
            })

            page.drawText(`${detail.species}`, {
                x: 75,
                y: height - heightOffset - (i * rowHeight),
                size: 14,
                font: helveticaFont,
                color: textColor,
            })

            page.drawText(`${detail.weigh_in_process_name}`, {
                x: 170,
                y: height - heightOffset - (i * rowHeight),
                size: 14,
                font: helveticaFont,
                color: textColor,
            })

            page.drawText(`${detail.fish_description ?? ""}`, {
                x: 40,
                y: height - (heightOffset + 15) - (i * rowHeight),
                size: 12,
                font: helveticaFont,
                color: textColor,
            })
        })
    } else {
        page.drawText(`HOLD FOR PICKUP`, {
            x: 40,
            y: height - 287 - (i * 29),
            size: 14,
            font: helveticaFont,
            color: textColor,
        })
    }

    page.drawText(order.total_weight_lb ? order.total_weight_lb + " LBS" : "", {
        x: 520,
        y: height - 189,
        size: 10,
        font: helveticaFont,
        color: textColor,
    })

    page.drawText(order.total_boxes + "", {
        x: 520,
        y: height - 217,
        size: 10,
        font: helveticaFont,
        color: textColor,
    })

    if (signaturePdfImage) {
        const imageDims = signaturePdfImage.scale(0.35)
        page.drawImage(signaturePdfImage, {
            x: 70,
            y: height - 748,
            width: imageDims.width,
            height: imageDims.height
        })
    }
}

async function tryEmbedSignaturePng(pdfDoc, blob, meta = {}) {
    if (!blob) {
        return null
    }
    if (!(blob instanceof Blob) || blob.size <= 0) {
        console.log("Skipping signature embed; empty or invalid blob.", {
            orderId: meta.orderId ?? null,
            size: blob?.size ?? null,
            type: blob?.type ?? null,
        })
        return null
    }
    try {
        const ab = await blob.arrayBuffer()
        return await pdfDoc.embedPng(ab)
    } catch (error) {
        console.log("Skipping signature embed; PNG embed failed.", {
            orderId: meta.orderId ?? null,
            size: blob?.size ?? null,
            type: blob?.type ?? null,
            error,
        })
        return null
    }
}

export async function generateBatchReceiptPdf(orderConfigs, locations, species, options, weighInProcesses) {
    const pdfDoc = await PDFDocument.create()
    const existingPdfBytes = await fetch(receiptPdf).then(res => res.arrayBuffer())
    const preloadedReceiptDoc = await PDFDocument.load(existingPdfBytes)
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
    const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

    for (let i = 0; i < orderConfigs.length; i++) {
        const pages = await pdfDoc.copyPages(preloadedReceiptDoc, [0])
        const preLoadedPage = pages[0]
        pdfDoc.addPage(preLoadedPage)

        const pdfImage = await tryEmbedSignaturePng(pdfDoc, orderConfigs[i].signatureBlob, {
            orderId: orderConfigs[i].order?.id,
        })
        generateReceiptPdfPage(preLoadedPage, helveticaFont, helveticaFontBold, orderConfigs[i].order, orderConfigs[i].master, locations, pdfImage, species, options, weighInProcesses)
    }

    return await pdfDoc.save()
}

export async function generateReceiptPdf(order, master, locations, signatureBlob, species, options, weighInProcesses) {
    const existingPdfBytes = await fetch(receiptPdf).then(res => res.arrayBuffer())
    const pdfDoc = await PDFDocument.load(existingPdfBytes)
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
    const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
    const pages = pdfDoc.getPages()
    const firstPage = pages[0]

    const pdfImage = await tryEmbedSignaturePng(pdfDoc, signatureBlob, { orderId: order?.id })
    generateReceiptPdfPage(firstPage, helveticaFont, helveticaFontBold, order, master, locations, pdfImage, species, options, weighInProcesses)

    return await pdfDoc.save()
}

export async function showReceiptPdfSimple(order, app) {
    const files = Array.isArray(order?.files) ? order.files : []
    const signatureUrl =
        files.find((f) => String(f?.type ?? "").trim().toLowerCase() === "signature")?.url ??
        (order?.signature_img ? ("/static/" + order.signature_img) : null)

    const signatureBlob = await fetchSignatureBlob(signatureUrl, {
        orderId: order?.id,
        context: "showReceiptPdfSimple",
    })
    await showReceiptPdf(order, order.master, app.locations, signatureBlob, app.species, app.options, app.weighInProcesses)
}

export async function showReceiptPdf(order, master, locations, signatureBlob, species, options, weighInProcesses) {
    const pdfBytes = await generateReceiptPdf(order, master, locations, signatureBlob, species, options, weighInProcesses)
    const file = new Blob([pdfBytes], { type: 'application/pdf' });
    var fileURL = URL.createObjectURL(file);
    window.open(fileURL);
}

function scale(width, factor, offset) {
    return Math.round(width * factor) + offset
}

function scaleDecimal(width, factor, offset) {
    return (width * factor) + offset
}

export async function generateBoxUpLabel(orderId, stJeansNum, firstName, lastName, boxes, boxTypes) {
    stJeansNum = stJeansNum.trim()
    const pdfDoc = await PDFDocument.create()
    const normal = await pdfDoc.embedFont(StandardFonts.Helvetica)
    const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
    const courier = await pdfDoc.embedFont(StandardFonts.Courier)
    const width = 600
    const aspectRatio = 0.583
    const height = scale(width, aspectRatio, 0)

    for (let i = 0; i < boxes.length; i++) {
        const page = pdfDoc.addPage([width, height])

        page.drawText("St Jean's Cannery & Smokehouse", {
            x: scale(width, 0.04, 0),
            y: scale(width, -0.06, height),
            size: scale(width, 0.04, 0),
            font: bold
        })

        page.drawText("Packed On:", {
            x: scale(width, 0.04, 0),
            y: scale(width, -0.11, height),
            size: scale(width, 0.04, 0),
            font: bold
        })

        const date = new Date()
        const month = ((date.getMonth() + 1) + "").padStart(2, '0')
        const day = (date.getDate() + "").padStart(2, '0')
        const year = date.getFullYear().toString().substring(2)
        const dateStr = day + "/" + month + "/" + year

        page.drawText(dateStr, {
            x: scale(width, 0.27, 0),
            y: scale(width, -0.11, height),
            size: scale(width, 0.04, 0),
            font: bold
        })

        page.drawText(stJeansNum, {
            x: scale(width, 0.03, 0),
            y: scale(width, -0.26, height),
            size: scale(width, 0.15, 0),
        })

        const qrCodeBuffer = await new Promise(resolve => {
            QRCode.toDataURL(`${boxes[i].id}`, { scale: 2 }, async function (err, url) {
                fetch(url).then(res => res.arrayBuffer()).then(resolve)
            })
        })

        const qrImage = await pdfDoc.embedPng(qrCodeBuffer)

        page.drawImage(qrImage, {
            x: scale(width, 0.73, 0),
            y: scale(width, -0.27, height),
            width: scale(width, 0.27, 0),
            height: scale(width, 0.27, 0),
        })

        const boxType = boxTypes.find(x => x.id === boxes[i].boxTypeId)
        const multiple = boxes.filter(x => x.boxTypeId === boxes[i].boxTypeId).length > 1
        const index = boxes.slice(0, i).filter(x => x.boxTypeId === boxes[i].boxTypeId).length + 1

        page.drawText(`${boxType.type}${multiple ? ` #${index}` : ``}`, {
            x: scale(width, 0.77, 0),
            y: scale(width, -0.263, height),
            size: scale(width, 0.025, 0),
            font: bold
        })

        const parts = nameNeedsMultiline(lastName, firstName)

        if (parts.length == 2) {
            page.drawText((parts[0]).toUpperCase(), {
                x: scale(width, 0.03, 0),
                y: scale(width, -0.325, height),
                size: scale(width, 0.06, 0),
            })
            page.drawText((parts[1]).toUpperCase(), {
                x: scale(width, 0.03, 0),
                y: scale(width, -0.385, height),
                size: scale(width, 0.06, 0),
            })
        } else {
            page.drawText((lastName + ", " + firstName).toUpperCase(), {
                x: scale(width, 0.03, 0),
                y: scale(width, -0.35, height),
                size: scale(width, 0.06, 0),
            })
        }

        page.drawText("Sport Caught not for resale", {
            x: scale(width, 0.04, 0),
            y: scale(width, -0.56, height),
            size: scale(width, 0.03, 0),
            font: bold
        })

        // Print CODE39 barcode of st jeans number
        const data = {}
        JsBarcode(data, stJeansNum, {
            format: "CODE39"
        })
        let x = scale(width, 0.03, 0)
        let y = scale(width, -0.5, height)
        for (var j = 0; j < data.encodings[0].data.length; j++) {
            const widthUnit = scaleDecimal(width, 0.0059, 0)
            if (data.encodings[0].data[j] === "1") {
                page.drawRectangle({
                    x: x,
                    y: y,
                    width: widthUnit,
                    height: scale(width, 0.1, 0),
                    color: rgb(0, 0, 0),
                })
            }
            x = x + widthUnit
        }

        // let barcodeText = "* "
        // for (let i = 0; i < stJeansNum.length; i++) {
        //     barcodeText += stJeansNum[i] + " "
        // }
        // barcodeText += "*"

        // firstPage.drawText(barcodeText, {
        //     x: scale(width, 0.69, 0),
        //     y: scale(width, -0.31, height),
        //     size: scale(width, 0.021, 0),
        //     font: courier
        // })

        const img = await fetch(productOfCanadaPng).then(res => res.blob())
        const imgArrayBuffer = await img.arrayBuffer()
        const pdfImage = await pdfDoc.embedPng(imgArrayBuffer)
        const imageDims = pdfImage.scale(0.17)
        page.drawImage(pdfImage, {
            x: scale(width, 0.62, 0),
            y: scale(width, -0.57, height),
            width: imageDims.width,
            height: imageDims.height
        })
    }

    return await pdfDoc.save()
}

function nameNeedsMultiline(lastName, firstName) {
    let name = `${lastName}, ${firstName}`
    if (name.length > 24) {
        // Try to split the name at the most expected place
        const words = name.split(" ")
        let charCount = name.length
        for (let i = words.length - 1; i > 0; i--) {
            charCount = charCount - (words[i].length + 1)
            if (charCount <= 24) {
                return [words.slice(0, i).join(" "), words.slice(i).join(" ")]
            }
        }
        // Last resort
        return [`${lastName},`, `${firstName}`]
    }

    return [name]
}

export async function showBoxUpLabel(orderId, stJeansNum, firstName, lastName, boxes, boxTypes) {
    const pdfBytes = await generateBoxUpLabel(orderId, stJeansNum, firstName, lastName, boxes, boxTypes)
    const file = new Blob([pdfBytes], { type: 'application/pdf' });
    var fileURL = URL.createObjectURL(file);
    window.open(fileURL);
}

// Source: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export function useInterval(callback, delay, runNow = false) {
    const savedCallback = useRef();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        let id;
        if (delay !== null) {
            id = setInterval(tick, delay);
        }
        if (runNow) {
            tick();
        }
        if (delay !== null) {
            return () => clearInterval(id);
        }
         
    }, [delay]);
}